<template>
  <modal-dialog
    visible
    persistent
    scrollable
    container-class="pa-0 pt-0"
    card-class="pa-0"
    :title="$t('label.message_guest')"
    @close="emit('close')"
  >
    <validation-observer ref="observer">
      <v-tabs
        v-model="messageType"
        fixed-tabs
        height="40"
        @change="body = isEmail ? emailBody : smsBody"
      >
        <v-tab :disabled="!reservation.contact.email">
          {{ $t('code.message_type.email') }}
        </v-tab>
        <v-tab :disabled="!reservation.contact.phone">
          {{ $t('code.message_type.sms') }}
        </v-tab>
      </v-tabs>
      <v-row class="ma-2 pt-6">
        <!-- EMAIL -->
        <template v-if="isEmail">
          <v-col
            cols="12"
            class="py-0 my-0"
          >
            <v-text-field
              class="py-0"
              :label="$t('label.email')"
              :value="reservation.contact.email"
              disabled
            />
          </v-col>
          <v-col
            cols="12"
            class="py-0 my-0"
          >
            <validated-text-field
              v-model="emailSubject"
              rules="required|max:250"
              class="py-0"
              :label="$t('label.subject')"
            />
          </v-col>
        </template>
        <!-- /// -->
        <!-- SMS Message -->
        <template v-else>
          <v-col cols="12">
            <v-text-field
              class="py-0"
              :label="$t('label.phone_number')"
              :value="reservation.contact.phone"
              disabled
            />
          </v-col>
        </template>
        <!--  /// -->
        <v-col cols="12">
          <rich-text-editor
            v-model="body"
            :richtext-allowed="isEmail"
            @input="updateMessage"
          >
            <template #menubar="{editor}">
              <v-menu offset-y>
                <template #activator="{ on }">
                  <v-btn
                    class="px-4 mr-2"
                    color="primary"
                    outlined
                    small
                    v-on="on"
                  >
                    {{ $t('label.insert_field') }}
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item
                    v-for="(field, i) in directMessageFields"
                    :key="i"
                    :disabled="isContentForField(field.value) === false"
                    @click="editor.commands.insertContent(contentForField(field.value));editor.commands.focus()"
                  >
                    <v-list-item-title>{{ field.text }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </rich-text-editor>
          <div
            v-if="isSMS"
            class="text--caption text-right pa-0 ma-0"
          >
            {{ smsMessagesCount }}
          </div>
        </v-col>
      </v-row>
    </validation-observer>

    <template #actions>
      <v-spacer />
      <v-btn
        color="success"
        :disabled="body.length === 0"
        @click="sendMessage"
      >
        {{ isEmail ? $t('label.send_email') : $t('label.send_sms') }}
      </v-btn>
      <v-btn
        color="error"
        text
        @click="emit('close')"
      >
        {{ $t('button.close') }}
      </v-btn>
    </template>
  </modal-dialog>
</template>

<script setup lang="ts">
import { computed, PropType, ref } from 'vue';
import RichTextEditor from '@/components/editors/RichTextEditor.vue';
import { count } from 'sms-length';
import i18n from '@/plugins/i18n';
import Reservation from '@/model/Reservation';
import Message, { MessageType } from '@/grpc-api/model/message';
import { performDispatchAction } from '@/services/vue-utils';
import localized from '@/plugins/vue-localized-formatter/src/localized';
import store from '@/store/store';
import toast from '@/plugins/toast-plugin/toast';
import { ValidationObserver } from 'vee-validate';

const emit = defineEmits(['close']);

const props = defineProps({
  reservation: { type: Object as PropType<Reservation>, required: true },
  feedbackId: { type: Number, required: false, default: undefined },
});

const messageTypes = [MessageType.EMAIL, MessageType.SMS];
const messageType = ref(props.reservation.contact.email ? 0 : 1);

const directMessageFields = [
  { text: i18n.t('code.handlebar.restaurant_name'), value: 'restaurant_name' },
  { text: i18n.t('code.handlebar.full_name'), value: 'full_name' },
  { text: i18n.t('code.handlebar.party_size'), value: 'party_size' },
  { text: i18n.t('code.handlebar.start_date'), value: 'start_date' },
  { text: i18n.t('code.handlebar.start_time'), value: 'start_time' },
  { text: i18n.t('code.handlebar.end_time'), value: 'end_time' },
  { text: i18n.t('code.handlebar.reservation_note'), value: 'reservation_note' },
];

const body = ref('');
const emailSubject = ref('');
const emailBody = ref('');
const smsBody = ref('');

const isEmail = computed(() => messageTypes[messageType.value] === MessageType.EMAIL);
const isSMS = computed(() => messageTypes[messageType.value] === MessageType.SMS);

const smsMessagesCount = computed(() => {
  const c = count(smsBody.value || '');
  return i18n.t(
    'label.sms_messages_count',
    [c.length, c.characterPerMessage * (c.messages || 1), c.messages],
  );
});

function contentForField(field: string): string {
  switch (field) {
    case 'restaurant_name': return ` ${store.state.settings.account.accountName} `;
    case 'full_name': return props.reservation.contact.name ? ` ${props.reservation.contact.name} ` : '';
    case 'party_size': return ` ${String(props.reservation.partySize)} `;
    case 'start_date': return ` ${localized.dateText(props.reservation.dateBegin)} `;
    case 'start_time': return ` ${localized.hourMinText(props.reservation.dateBegin)} `;
    case 'end_time': return ` ${localized.hourMinText(props.reservation.dateEnd)} `;
    case 'reservation_note': return props.reservation.notes ? ` ${props.reservation.notes} ` : '';
    default: return '';
  }
}

function isContentForField(field: string): boolean {
  return contentForField(field) !== '';
}

function updateMessage(value: string) {
  if (isEmail.value) {
    emailBody.value = value;
  } else {
    smsBody.value = value;
  }
}

const observer = ref<InstanceType<typeof ValidationObserver>>();

async function sendMessage() {
  const message = new Message();
  message.reservationId = props.reservation.id;
  message.feedbackId = props.feedbackId;
  message.sendBcc = false;

  if (isEmail.value) {
    message.messageType = MessageType.EMAIL;
    message.subject = emailSubject.value;
    message.body = emailBody.value;
  } else {
    message.messageType = MessageType.SMS;
    message.subject = emailSubject.value;
    message.body = smsBody.value;
  }
  if (!await observer.value!.validate()) {
    toast.error(i18n.tc('error.validation_failed'));
    return;
  }

  const ok = await performDispatchAction('sendReservationMessage', { message });
  console.log('sendMessage: ', ok);
  if (!ok) return;
  toast.success(i18n.tc('message.message_successfuly_sent'));
  emit('close', true);
}

</script>
